import { ShaderStage } from '../../../shared/shader/const';

import type { ProgramPipelineLayoutDescription } from '../GpuProgram';
import type { StructsAndGroups } from './extractStructAndGroups';

/**
 * Maps a WGSL texture declaration's texel type to the bind group layout sample type
 * @param type - The WGSL binding type, e.g. `texture_2d<u32>`.
 * @returns The matching bind group layout sample type.
 */
function getTextureSampleType(type: string): GPUTextureSampleType
{
    if (type.endsWith('<u32>'))
    {
        return 'uint';
    }

    if (type.endsWith('<i32>'))
    {
        return 'sint';
    }

    return 'float';
}

/**
 * Generates the default WebGPU bind group layout for a shader from its extracted structs and groups.
 * Every binding is marked visible to both the vertex and fragment stages
 * (`GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT`).
 *
 * This is the same generator {@link GpuProgram} uses internally when no `gpuLayout` is supplied.
 * It is exported so you can build the default layout, tweak only the entries you care about
 * (for example narrowing a binding's `visibility` to a single stage), and pass the complete
 * result back in via the `gpuLayout` option - rather than hand-authoring the whole layout:
 * @example
 * import { extractStructAndGroups, generateGpuLayoutGroups, GpuProgram } from 'pixi.js';
 *
 * const gpuLayout = generateGpuLayoutGroups(extractStructAndGroups(source));
 *
 * // only expose this texture to the fragment stage
 * gpuLayout[0][1].visibility = GPUShaderStage.FRAGMENT;
 *
 * const program = new GpuProgram({ vertex, fragment, gpuLayout });
 * @param root0 - The structs and groups extracted from the shader source, typically produced by
 * {@link extractStructAndGroups}.
 * @param root0.groups - The `@group`/`@binding` entries parsed from the WGSL source.
 * @returns The generated bind group layout description, one entry array per bind group.
 * @category rendering
 * @advanced
 */
export function generateGpuLayoutGroups({ groups }: StructsAndGroups): ProgramPipelineLayoutDescription
{
    const layout: ProgramPipelineLayoutDescription = [];

    for (let i = 0; i < groups.length; i++)
    {
        const group = groups[i];

        if (!layout[group.group])
        {
            layout[group.group] = [];
        }

        if (group.accessMode === 'uniform')
        {
            layout[group.group].push({
                binding: group.binding,
                visibility: ShaderStage.VERTEX | ShaderStage.FRAGMENT,
                buffer: {
                    type: 'uniform'
                }
            });
        }
        else if (group.accessMode === 'storage')
        {
            layout[group.group].push({
                binding: group.binding,
                visibility: ShaderStage.VERTEX | ShaderStage.FRAGMENT,
                buffer: {
                    type: 'read-only-storage'
                }
            });
        }
        else if (group.type === 'sampler')
        {
            layout[group.group].push({
                binding: group.binding,
                visibility: ShaderStage.VERTEX | ShaderStage.FRAGMENT,
                sampler: {
                    type: 'filtering'
                }
            });
        }
        else if (group.type === 'sampler_comparison')
        {
            layout[group.group].push({
                binding: group.binding,
                visibility: ShaderStage.VERTEX | ShaderStage.FRAGMENT,
                sampler: {
                    type: 'comparison'
                }
            });
        }
        else if (group.type === 'texture_2d' || group.type.startsWith('texture_2d<'))
        {
            layout[group.group].push({
                binding: group.binding,
                visibility: ShaderStage.VERTEX | ShaderStage.FRAGMENT,
                texture: {
                    sampleType: getTextureSampleType(group.type),
                    viewDimension: '2d',
                    multisampled: false,
                }
            });
        }
        else if (group.type === 'texture_depth_2d')
        {
            layout[group.group].push({
                binding: group.binding,
                visibility: ShaderStage.VERTEX | ShaderStage.FRAGMENT,
                texture: {
                    sampleType: 'depth',
                    viewDimension: '2d',
                    multisampled: false,
                }
            });
        }
        else if (group.type === 'texture_depth_2d_array')
        {
            layout[group.group].push({
                binding: group.binding,
                visibility: ShaderStage.VERTEX | ShaderStage.FRAGMENT,
                texture: {
                    sampleType: 'depth',
                    viewDimension: '2d-array',
                    multisampled: false,
                }
            });
        }
        else if (group.type === 'texture_2d_array' || group.type.startsWith('texture_2d_array<'))
        {
            layout[group.group].push({
                binding: group.binding,
                visibility: ShaderStage.VERTEX | ShaderStage.FRAGMENT,
                texture: {
                    sampleType: getTextureSampleType(group.type),
                    viewDimension: '2d-array',
                    multisampled: false,
                }
            });
        }
        else if (group.type === 'texture_3d' || group.type.startsWith('texture_3d<'))
        {
            layout[group.group].push({
                binding: group.binding,
                visibility: ShaderStage.VERTEX | ShaderStage.FRAGMENT,
                texture: {
                    sampleType: getTextureSampleType(group.type),
                    viewDimension: '3d',
                    multisampled: false,
                }
            });
        }
        else if (group.type === 'texture_cube' || group.type.startsWith('texture_cube<'))
        {
            layout[group.group].push({
                binding: group.binding,
                visibility: ShaderStage.VERTEX | ShaderStage.FRAGMENT,
                texture: {
                    sampleType: getTextureSampleType(group.type),
                    viewDimension: 'cube',
                    multisampled: false,
                }
            });
        }
    }

    // Ensure a dense array. WebGPU expects intermediate bind groups to exist even if empty.
    for (let i = 0; i < layout.length; i++)
    {
        layout[i] ||= [];
    }

    return layout;
}
